import Card from '@mui/joy/Card'
import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import Button from '@mui/joy/Button'
import Instagram from '@mui/icons-material/Instagram'
import Facebook from '@mui/icons-material/Facebook'
import X from '@mui/icons-material/X'
import Pinterest from '@mui/icons-material/Pinterest'
import LinkedIn from '@mui/icons-material/LinkedIn'
import YouTube from '@mui/icons-material/YouTube'
import { Loading } from './ui/Loading'
import { useMutation, useQuery } from '@tanstack/react-query'
import { NetworkAPI } from '../api/NetworkAPI'
import { OAUTH_CONNECTION_REQUEST_SOURCE } from '../consts'

const NETWORKS = [
    {
        displayName: 'Instagram',
        canonicalName: 'instagram',
        enabled: true,
        account_type: 0,
        icon: <Instagram sx={{ color: '#d62976' }} />,
    },
    {
        displayName: 'Facebook',
        canonicalName: 'facebook_page',
        enabled: true,
        account_type: 1,
        icon: <Facebook sx={{ color: '#1166ff' }} />,
    },
    {
        displayName: 'YouTube',
        canonicalName: 'youtube_channel',
        enabled: true,
        account_type: 2,
        icon: <YouTube sx={{ color: '#ff0000' }} />,
    },
    {
        displayName: 'X (Twitter)',
        canonicalName: 'twitter',
        enabled: false,
        icon: <X />,
    },
    {
        displayName: 'Pinterest',
        canonicalName: 'pinterest',
        enabled: false,
        icon: <Pinterest />,
    },
    {
        displayName: 'LinkedIn',
        canonicalName: 'linkedin',
        enabled: false,
        icon: <LinkedIn />,
    },
]

const UnavailableNetworkButton = ({ network }) => {
    return (
        <Button
            disabled
            variant="outlined"
            sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                gap: 2,
                p: 1,
            }}
            key={network.canonicalName}
        >
            {network.icon}
            <Typography level="body-md">{network.displayName}</Typography>
            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'flex-end' }}>
                <Typography level="body-xs">Coming Soon!</Typography>
            </Box>
        </Button>
    )
}

const ConnectedNetworkButton = ({ network }) => {
    const targetNetwork = NETWORKS.find(
        (item) => network.type === item.canonicalName
    )
    return (
        <Button
            disabled
            variant="outlined"
            sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                gap: 2,
                p: 1,
            }}
        >
            {targetNetwork.icon}
            <Typography level="body-md">{targetNetwork.displayName}</Typography>
            <Box sx={{ display: 'flex', flex: 1, justifyContent: 'flex-end' }}>
                <Typography level="body-xs" color="success">
                    Connected to {network.username || network.name}{' '}
                </Typography>
            </Box>
        </Button>
    )
}

const NetworkButton = ({ network, onClick }) => {
    return network.enabled ? (
        <Button
            variant="outlined"
            sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                gap: 2,
                p: 1,
            }}
            onClick={() =>
                onClick({
                    source: OAUTH_CONNECTION_REQUEST_SOURCE.ONBOARDING,
                    account_type: network.account_type,
                })
            }
        >
            {network.icon}
            <Typography level="body-md">{network.displayName}</Typography>
        </Button>
    ) : (
        <UnavailableNetworkButton network={network} />
    )
}

const NetworksList = ({ connectedNetworks, connectAccount }) => {
    return NETWORKS.map((network) => {
        // if this network is in the connected networks render a connected button, else render based on
        // enabled status
        const connection = connectedNetworks.find((connectedNetwork) => {
            return network.canonicalName === connectedNetwork.type
        })

        if (connection) {
            return (
                <ConnectedNetworkButton
                    key={network.canonicalName}
                    network={connection}
                />
            )
        }
        return (
            <NetworkButton
                key={network.displayName}
                network={network}
                onClick={connectAccount}
            />
        )
    })
}

export const NetworkConnectionForm = ({ onNext, connectAccount }) => {
    const { isPending, data } = useQuery({
        queryKey: ['connectedNetworks'],
        queryFn: NetworkAPI.getAccounts,
    })
    const connected = data ? data.data.accounts : null
    const hasConnections = connected !== null && connected.length > 0

    return isPending ? (
        <Loading message="Getting networks..." />
    ) : (
        <Card sx={{ width: 350, gap: 2 }} variant="outlined" color="neutral">
            <Box>
                <Typography level="title-lg">Add Networks</Typography>
                <Typography level="body-xs">
                    Connect multiple networks
                </Typography>
            </Box>
            <Box
                sx={{
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <NetworksList
                    connectedNetworks={connected}
                    connectAccount={connectAccount}
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'flex-end',
                    gap: 2,
                }}
            >
                <Button
                    variant="plain"
                    color="primary"
                    disabled={hasConnections}
                    onClick={onNext}
                >
                    Skip
                </Button>
                <Button
                    variant="solid"
                    color="primary"
                    disabled={!hasConnections}
                    onClick={onNext}
                >
                    Next
                </Button>
            </Box>
        </Card>
    )
}
